import * as yup from 'yup';

import i18n from '../../../../i18n/configs';
import { passwordValidataion } from '../../auth/validation';

const emailValidation = yup
  .string()
  .lowercase()
  .required(i18n.t('validation.error.required'))
  .email(i18n.t('validation.error.email'))
  .matches(
    /^(?!.*(^[\w.+]+(@gmail\.com|@yahoo\.co\.jp|@aol\.jp|@icloud\.com|@outlook\.jp|@outlook\.com|@hotmail\.com|@live\.jp|@docomo\.ne\.jp|@ezweb\.ne\.jp|@softbank\.ne\.jp|@[\w.+]+.vodafone\.ne\.jp)$)).+$/,
    i18n.t('validation.error.specificEmail')
  );

const urlValidation = yup.string().url(i18n.t('validation.error.url'));

const requiredValidation = yup
  .string()
  .required(i18n.t('validation.error.required'));

const imageValidation = yup
  .mixed()
  .test('type', i18n.t('validation.error.image'), (value: FileList): boolean =>
    value
      ? String(value).includes('.jpg') ||
        String(value).includes('.jpeg') ||
        String(value).includes('.png') ||
        String(value).includes('.webp') ||
        String(value).includes('.gif') ||
        String(value).includes('.svg') ||
        String(value).includes('.JPG') ||
        String(value).includes('.JPEG') ||
        String(value).includes('.PNG') ||
        String(value).includes('.WEBP') ||
        String(value).includes('.GIF') ||
        String(value).includes('.SVG')
      : true
  );

export const publisherRegisterSchema = yup.object().shape({
  address: yup.string().max(250, i18n.t('validation.error.below250')),
  bgImage: imageValidation,
  capital: yup
    .number()
    .nullable()
    .transform(
      (value, originalValue) =>
        (isNaN(Number(originalValue))
          ? originalValue
          : Number(originalValue)) as number
    )
    .test(
      'is-number',
      i18n.t('validation.error.number'),
      (value) =>
        value === undefined ||
        value === null ||
        (value !== undefined && value !== null && !isNaN(Number(value)))
    )
    .min(0, i18n.t('validation.error.above1'))
    .max(1000000000000000, i18n.t('validation.error.below1000000000000000'))
    .typeError(i18n.t('validation.error.number')),
  city: requiredValidation.max(250, i18n.t('validation.error.below250')),
  companyName: requiredValidation.max(250, i18n.t('validation.error.below250')),
  companyUrl: urlValidation
    .required(i18n.t('validation.error.required'))
    .max(250, i18n.t('validation.error.below250')),
  industryType: yup.number(),
  leaderName: yup.string().max(250, i18n.t('validation.error.below250')),
  listing: yup.bool().oneOf([true, false], i18n.t('validation.error.required')),
  logo: yup
    .mixed()
    .test(
      'type',
      i18n.t('validation.error.image'),
      (value: FileList): boolean =>
        value
          ? String(value).includes('.jpg') ||
            String(value).includes('.jpeg') ||
            String(value).includes('.png') ||
            String(value).includes('.webp') ||
            String(value).includes('.gif') ||
            String(value).includes('.svg') ||
            String(value).includes('.JPG') ||
            String(value).includes('.JPEG') ||
            String(value).includes('.PNG') ||
            String(value).includes('.WEBP') ||
            String(value).includes('.GIF') ||
            String(value).includes('.SVG')
          : true
    ),
  market: yup.number(),
  numbers: yup
    .number()
    .nullable()
    .transform(
      (value, originalValue) =>
        (isNaN(Number(originalValue))
          ? originalValue
          : Number(originalValue)) as number
    )
    .test(
      'is-number',
      i18n.t('validation.error.number'),
      (value) =>
        value === undefined ||
        value === null ||
        (value !== undefined && value !== null && !isNaN(Number(value)))
    )
    .min(0, i18n.t('validation.error.above1'))
    .max(100000000, i18n.t('validation.error.below100000000'))
    .typeError(i18n.t('validation.error.number')),
  password: passwordValidataion,
  prefecture: yup.number(),
  representativeEmail: emailValidation.max(
    250,
    i18n.t('validation.error.below250')
  ),
  representativeName: requiredValidation.max(
    250,
    i18n.t('validation.error.below250')
  ),
  resources_0: urlValidation.max(250, i18n.t('validation.error.below250')),
  resources_1: urlValidation.max(250, i18n.t('validation.error.below250')),
  resources_2: urlValidation.max(250, i18n.t('validation.error.below250')),
  resources_3: urlValidation.max(250, i18n.t('validation.error.below250')),
  resources_4: urlValidation.max(250, i18n.t('validation.error.below250')),
  sns_0: urlValidation.max(250, i18n.t('validation.error.below250')),
  sns_1: urlValidation.max(250, i18n.t('validation.error.below250')),
  sns_2: urlValidation.max(250, i18n.t('validation.error.below250')),
  sns_3: urlValidation.max(250, i18n.t('validation.error.below250')),
  sns_4: urlValidation.max(250, i18n.t('validation.error.below250')),
  yearEstablished: yup
    .number()
    .transform((value: number) => (isNaN(value) ? undefined : value))
    .nullable()
    .min(999, i18n.t('validation.error.yearEstablished'))
    .max(10000, i18n.t('validation.error.yearEstablished'))
    .required(i18n.t('validation.error.required')),
  zipcode: yup
    .string()
    .matches(/^[0-9]{7}$/, i18n.t('validation.error.zipcode'))
});

export const clientRegisterSchema = yup.object().shape({
  companyName: requiredValidation.max(250, i18n.t('validation.error.below250')),
  companyUrl: urlValidation
    .required(i18n.t('validation.error.required'))
    .max(250, i18n.t('validation.error.below250')),
  department: yup.string().max(250, i18n.t('validation.error.below250')),
  email: emailValidation,
  industry: yup.number().required(i18n.t('validation.error.required')),
  industryOther: yup.string().when('industry', {
    is: 99,
    otherwise: yup.string().nullable(),
    then: requiredValidation
      .transform((value: string | null) => (value === null ? '' : value))
      .max(250, i18n.t('validation.error.below250'))
  }),
  password: passwordValidataion,
  representativeName: requiredValidation.max(
    250,
    i18n.t('validation.error.below250')
  )
});

export const agentClientRegisterSchema = yup.object().shape({
  companyName: requiredValidation.max(250, i18n.t('validation.error.below250')),
  companyUrl: urlValidation
    .required(i18n.t('validation.error.required'))
    .max(250, i18n.t('validation.error.below250')),
  industry: yup.number().min(1, i18n.t('validation.error.required')),
  industryOther: yup.string().when('industry', {
    is: 99,
    otherwise: yup.string().nullable(),
    then: yup
      .string()
      .required(i18n.t('validation.error.required'))
      .transform((value: string | null) => (value === null ? '' : value))
      .max(250, i18n.t('validation.error.below250'))
  })
});

export const clientStaffRegisterSchema = yup.object().shape({
  department: requiredValidation.max(250, i18n.t('validation.error.below250')),
  icon: imageValidation,
  name: requiredValidation.max(250, i18n.t('validation.error.below250')),
  password: passwordValidataion
});

export const clientStaffInviteSchema = yup.object().shape({
  email: emailValidation.max(250, i18n.t('validation.error.below250')),
  role: yup.bool().oneOf([true, false], i18n.t('validation.error.required'))
});

export const clientStaffEditSchema = yup.object().shape({
  department: yup
    .string()
    .nullable()
    .required(i18n.t('validation.error.required'))
    .max(250, i18n.t('validation.error.below250')),
  icon: imageValidation,
  name: requiredValidation.max(250, i18n.t('validation.error.below250')),
  role: yup.bool().oneOf([true, false], i18n.t('validation.error.required'))
});
